import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, TextField, Typography, Alert, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

const EmailConfiguration: React.FC = () => {
  const [accountEmails, setAccountEmails] = useState('');
  const [overviewEmails, setOverviewEmails] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [overviewFreq, setOverviewFreq] = useState<'off' | 'daily' | 'weekly'>('daily');
  const [overviewTime, setOverviewTime] = useState('08:00');
  const [overviewDow, setOverviewDow] = useState<number>(1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('token');
        const businessId = localStorage.getItem('businessId');
        if (!token || !businessId) {
          setError('Not authenticated or business not selected.');
          return;
        }
        const res = await axios.get(`${API_BASE_URL}/businesses/${businessId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAccountEmails(res.data.account_emails || '');
        setOverviewEmails(res.data.overview_emails || '');
        setOverviewFreq((res.data.overview_email_frequency as any) || 'daily');
        setOverviewTime(res.data.overview_email_time || '08:00');
        setOverviewDow(typeof res.data.overview_email_day_of_week === 'number' ? res.data.overview_email_day_of_week : 1);
      } catch (err) {
        console.error('Failed to load email settings', err);
        setError('Failed to load email settings.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const validateEmail = (email: string) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  const validateList = (value: string) =>
    value
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.length > 0)
      .every((e) => validateEmail(e));

  const handleSave = async () => {
    try {
      setLoading(true);
      setSuccess(false);
      setError(null);
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');
      if (!token || !businessId) {
        setError('Not authenticated or business not selected.');
        return;
      }
      if (accountEmails.trim().length > 0 && !validateList(accountEmails)) {
        setError('Account emails contain an invalid address.');
        return;
      }
      if (overviewEmails.trim().length > 0 && !validateList(overviewEmails)) {
        setError('Overview emails contain an invalid address.');
        return;
      }
      await axios.put(
        `${API_BASE_URL}/businesses/${businessId}/email-settings`,
        {
          account_emails: accountEmails.trim() || null,
          overview_emails: overviewEmails.trim() || null,
          overview_email_frequency: overviewFreq,
          overview_email_time: overviewTime,
          overview_email_day_of_week: overviewFreq === 'weekly' ? overviewDow : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save email settings', err);
      setError('Failed to save email settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendNow = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');
      if (!token || !businessId) {
        setError('Not authenticated or business not selected.');
        return;
      }
      await axios.post(
        `${API_BASE_URL}/businesses/${businessId}/send-overview-digest`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Failed to send overview digest now', err);
      setError(err?.response?.data?.message || 'Failed to send overview email.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Email Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter comma-separated email addresses for each field.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Saved successfully.
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Account Emails (comma-separated)"
            value={accountEmails}
            onChange={(e) => setAccountEmails(e.target.value)}
            placeholder="accounts@acme.com, billing@acme.com"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Overview Emails (comma-separated)"
            value={overviewEmails}
            onChange={(e) => setOverviewEmails(e.target.value)}
            placeholder="overview@acme.com, ops@acme.com"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Overview Frequency</InputLabel>
            <Select value={overviewFreq} label="Overview Frequency" onChange={(e) => setOverviewFreq(e.target.value as any)}>
              <MenuItem value="off">Off</MenuItem>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Send Time (HH:MM)"
            value={overviewTime}
            onChange={(e) => setOverviewTime(e.target.value)}
            placeholder="08:00"
            inputProps={{ maxLength: 5 }}
          />
        </Grid>
        {overviewFreq === 'weekly' && (
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Day of Week</InputLabel>
              <Select value={overviewDow} label="Day of Week" onChange={(e) => setOverviewDow(Number(e.target.value))}>
                <MenuItem value={0}>Sunday</MenuItem>
                <MenuItem value={1}>Monday</MenuItem>
                <MenuItem value={2}>Tuesday</MenuItem>
                <MenuItem value={3}>Wednesday</MenuItem>
                <MenuItem value={4}>Thursday</MenuItem>
                <MenuItem value={5}>Friday</MenuItem>
                <MenuItem value={6}>Saturday</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, gap: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" color="secondary" onClick={handleSendNow} disabled={loading || !overviewEmails.trim()}>
              {loading ? 'Working...' : 'Send Overview Now'}
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EmailConfiguration;


